// js/common/actions/share-button.js
if (!window.customElements.get('share-button')) {
    var ShareButton = class extends HTMLButtonElement {
        constructor() {
            super();
            if (navigator.share) {
                this.addEventListener('click', this._showSystemShare);
            }
        }
        _showSystemShare() {
            navigator.share({
                title: this.hasAttribute('share-title') ? this.getAttribute('share-title') : document.title,
                url: this.hasAttribute('share-url') ? this.getAttribute('share-url') : window.location.href,
            });
        }
    };
    window.customElements.define('share-button', ShareButton, { extends: 'button' });
}

// js/common/form/resizable-textarea.js
if (!window.customElements.get('resizable-textarea')) {
    var ResizableTextarea = class extends HTMLTextAreaElement {
        constructor() {
            super();
            this.addEventListener('input', this._onInput);
        }
        _onInput() {
            this.style.height = 'auto';
            this.style.height = this.scrollHeight + 2 + 'px';
        }
    };
    window.customElements.define('resizable-textarea', ResizableTextarea, { extends: 'textarea' });
}

if (!window.customElements.get('product-zoom-button')) {
    var ProductZoomButton = class extends HTMLButtonElement {
        constructor() {
            super();
            this.addEventListener('click', this._onButtonClicked);
        }
        _onButtonClicked() {
            let media = this.closest('.product-gallery__media'),
                openIndex;
            if (media) {
                const visibleImages = this.closest('media-carousel').visibleItems.filter((item) => item.getAttribute('data-media-type') === 'image');
                openIndex = visibleImages.indexOf(media);
            } else {
                const carousel = this.closest('.product-gallery__media-list-wrapper').querySelector('media-carousel'),
                    visibleImages = carousel.visibleItems.filter((item) => item.getAttribute('data-media-type') === 'image');
                openIndex = visibleImages.indexOf(carousel.selectedSlide);
            }
            this.dispatchEvent(
                new CustomEvent('lightbox:open', {
                    bubbles: true,
                    detail: {
                        index: openIndex,
                    },
                })
            );
        }
    };

    window.customElements.define('product-zoom-button', ProductZoomButton, { extends: 'button' });
}

// js/common/product/quick-buy-drawer.js
import { animate as animate7, timeline as timeline5 } from 'vendor';
if (!window.customElements.get('quick-buy-drawer')) {
    var QuickBuyDrawer = class extends window.Drawer {
        constructor() {
            super();
            this._hasLoaded = false;
            this.addEventListener('variant:add', this._onVariantAdded.bind(this));
        }
        async show() {
            this.style.display = 'block';
            if (!this._hasLoaded) {
                [this, ...this.controls].forEach((control) => control.setAttribute('aria-busy', 'true'));
                const responseContent = await (await fetch(`${window.Shopify.routes.root}products/${this.getAttribute('handle')}`)).text();
                [this, ...this.controls].forEach((control) => control.setAttribute('aria-busy', 'false'));
                const quickBuyContent = new DOMParser().parseFromString(responseContent, 'text/html').getElementById('quick-buy-content').content;
                Array.from(quickBuyContent.querySelectorAll('noscript')).forEach((noScript) => noScript.remove());
                this.replaceChildren(quickBuyContent);
                Shopify.PaymentButton?.init();
                this._hasLoaded = true;
            }
            return super.show();
        }
        async hide() {
            return super.hide()?.then(() => {
                this.style.display = 'none';
            });
        }
        _onVariantAdded(event) {
            event.detail.blockCartDrawerOpening = true;
            const contentShadow = this.shadowRoot.querySelector('[part="content"]'),
                fromHeight = contentShadow.clientHeight;
            animate7(contentShadow.children, { opacity: 0, visibility: 'hidden' }, { duration: 0.15 });
            this.replaceChildren(
                ...new DOMParser().parseFromString(event.detail.cart['sections']['variant-added'], 'text/html').querySelector('.shopify-section').children
            );
            requestAnimationFrame(async () => {
                await timeline5([
                    [contentShadow, { height: [`${fromHeight}px`, `${contentShadow.clientHeight}px`] }, { duration: 0.35, easing: [0.86, 0, 0.07, 1] }],
                    [contentShadow.children, { opacity: [0, 1], visibility: 'visible' }, { duration: 0.15 }],
                ]).finished;
                contentShadow.style.height = null;
            });
            this._hasLoaded = false;
        }
    };
    window.customElements.define('quick-buy-drawer', QuickBuyDrawer);
}

// js/common/media/model.js
if (!window.customElements.get('model-media')) {
    var ModelMedia = class extends BaseMedia {
        connectedCallback() {
            super.connectedCallback();
            this.player;
        }
        _playerTarget() {
            return new Promise((resolve) => {
                this.setAttribute('loaded', '');
                window.Shopify.loadFeatures([
                    {
                        name: 'shopify-xr',
                        version: '1.0',
                        onLoad: this._setupShopifyXr.bind(this),
                    },
                    {
                        name: 'model-viewer-ui',
                        version: '1.0',
                        onLoad: () => {
                            const modelViewer = this.querySelector('model-viewer');
                            modelViewer.addEventListener('shopify_model_viewer_ui_toggle_play', () => this.setAttribute('playing', ''));
                            modelViewer.addEventListener('shopify_model_viewer_ui_toggle_pause', () => this.removeAttribute('playing'));
                            this.setAttribute('can-play', '');
                            resolve(new window.Shopify.ModelViewerUI(modelViewer, { focusOnPlay: true }));
                        },
                    },
                ]);
            });
        }
        _playerHandler(target, prop) {
            target[prop]();
        }
        async _setupShopifyXr() {
            if (!window.ShopifyXR) {
                document.addEventListener('shopify_xr_initialized', this._setupShopifyXr.bind(this));
            } else {
                const models = (await ProductLoader.load(this.getAttribute('handle')))['media'].filter((media) => media['media_type'] === 'model');
                window.ShopifyXR.addModels(models);
                window.ShopifyXR.setupXRElements();
            }
        }
    };
    window.customElements.define('model-media', ModelMedia);
}

if (!window.customElements.get('mega-menu-promo-carousel')) {
    var MegaMenuPromoCarousel = class extends window.EffectCarousel {
        connectedCallback() {
            super.connectedCallback();
            if (this.nextElementSibling) {
                this.addEventListener('carousel:select', (event) => this._updateControlsColor(event.detail.slide));
            }
            this._updateControlsColor(this.items[this.selectedIndex]);
        }
        _updateControlsColor(slide) {
            const extractFrom = slide.classList.contains('content-over-media') ? slide : slide.firstElementChild;
            this.nextElementSibling.style.setProperty('--text-color', extractFrom.style.getPropertyValue('--text-color'));
        }
    };

    window.customElements.define('mega-menu-promo-carousel', MegaMenuPromoCarousel);
}

// js/common/actions/copy-button.js
import { timeline as timeline6 } from 'vendor';
if (!window.customElements.get('copy-button')) {

    var CopyButton = class extends HTMLButtonElement {
        constructor() {
            super();
            this.append(this.contentElement, this.animationElement);
            this.addEventListener('click', this._copyToClipboard.bind(this));
        }
        get contentElement() {
            if (this._contentElement) {
                return this._contentElement;
            }
            this._contentElement = document.createElement('div');
            this._contentElement.append(...this.childNodes);
            return (this._contentElement = this._contentElement || document.createElement('div').append());
        }
        get animationElement() {
            return (this._animationElement =
                this._animationElement ||
                document.createRange().createContextualFragment(`
      <span class="button__feedback">  
        <svg role="presentation" focusable="false" fill="none" width="18px" height="18px" viewBox="0 0 20 20">
          <circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="2"/>
          <path d="m6 9.8 2.63 2.8L14 7" stroke="currentColor" stroke-width="2"/>
        </svg>
      </span>
    `).firstElementChild);
        }
        async _copyToClipboard() {
            if (!navigator.clipboard) {
                return;
            }
            await navigator.clipboard.writeText(this.getAttribute('data-text') || '');
            timeline6([
                [this.contentElement, { y: ['translateY(0)', 'translateY(-10px)'], opacity: 0 }, { duration: 0.15 }],
                [this.animationElement, { transform: ['translateY(10px)', 'translateY(0)'], opacity: [0, 1] }, { duration: 0.15 }],
                [this.animationElement, { transform: ['translateY(0)', 'translateY(-10px)'], opacity: 0 }, { duration: 0.15, at: '+0.5' }],
                [this.contentElement, { transform: ['translateY(10px)', 'translateY(0)'], opacity: 1 }, { duration: 0.15 }],
            ]);
        }
    };
    window.customElements.define('copy-button', CopyButton, { extends: 'button' });
}

// js/common/actions/confirm-button.js
if (!window.customElements.get('confirm-button')) {
    var ConfirmButton = class extends HTMLButtonElement {
        constructor() {
            super();
            this.addEventListener('click', (event) => {
                if (!window.confirm(this.getAttribute('data-message') ?? 'Are you sure you wish to do this?')) {
                    event.preventDefault();
                }
            });
        }
    };
    window.customElements.define('confirm-button', ConfirmButton, { extends: 'button' });
}

// js/common/utilities/country-selector.js
if (!window.customElements.get('country-selector')) {
    var CountrySelector = class extends HTMLElement {
        constructor() {
            super();
            this._onCountryChangedListener = this._onCountryChanged.bind(this);
        }
        connectedCallback() {
            this.countryElement = this.querySelector('[name="address[country]"]');
            this.provinceElement = this.querySelector('[name="address[province]"]');
            this.countryElement.addEventListener('change', this._onCountryChangedListener);
            if (this.hasAttribute('country') && this.getAttribute('country') !== '') {
                this.countryElement.selectedIndex = Math.max(
                    0,
                    Array.from(this.countryElement.options).findIndex((option) => option.textContent === this.getAttribute('country'))
                );
                this.countryElement.dispatchEvent(new Event('change'));
            } else {
                this._onCountryChanged();
            }
        }
        disconnectedCallback() {
            this.countryElement.removeEventListener('change', this._onCountryChangedListener);
        }
        _onCountryChanged() {
            const option = this.countryElement.options[this.countryElement.selectedIndex],
                provinces = JSON.parse(option.getAttribute('data-provinces'));
            this.provinceElement.parentElement.hidden = provinces.length === 0;
            if (provinces.length === 0) {
                return;
            }
            this.provinceElement.innerHTML = '';
            provinces.forEach((data) => {
                const selected = data[1] === this.getAttribute('province');
                this.provinceElement.options.add(new Option(data[1], data[0], selected, selected));
            });
        }
    };
    window.customElements.define('country-selector', CountrySelector);
}

// js/common/cart/cart-note.js
if (!window.customElements.get('cart-note')) {
    var CartNote = class extends HTMLElement {
        constructor() {
            super();
            this.addEventListener('change', this._onNoteChanged);
        }
        _onNoteChanged(event) {
            if (event.target.getAttribute('name') !== 'note') {
                return;
            }
            fetch(`${Shopify.routes.root}cart/update.js`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ note: event.target.value }),
                keepalive: true,
                // Allows to make sure the request is fired even when submitting the form
            });
        }
    };

    window.customElements.define('cart-note', CartNote);
}

import { DialogElement } from 'theme';
if (!window.customElements.get('cart-note-dialog')) {
    var CartNoteDialog = class extends DialogElement {
        constructor() {
            super();
            this.addEventListener('change', this._onNoteChanged);
        }
        _onNoteChanged(event) {
            if (event.target.value === '') {
                this.controls.forEach(
                    (control) => (control.innerHTML = `<span class="link text-sm text-subdued">${window.themeVariables.strings.addOrderNote}</span>`)
                );
            } else {
                this.controls.forEach(
                    (control) => (control.innerHTML = `<span class="link text-sm text-subdued">${window.themeVariables.strings.editOrderNote}</span>`)
                );
            }
        }
    };

    window.customElements.define('cart-note-dialog', CartNoteDialog);
}

if (!window.customElements.get('line-item')) {
    var LineItem = class extends HTMLElement {
        connectedCallback() {
            this.pillLoaderElement = this.querySelector('pill-loader');
            this.addEventListener('line-item:will-change', this._onWillChange.bind(this));
            this.addEventListener('line-item:error', this._onErrored.bind(this));
            this.addEventListener('line-item:change', this._onChanged.bind(this));
        }
        _onWillChange() {
            this.pillLoaderElement.setAttribute('aria-busy', 'true');
        }
        _onErrored() {
            this.pillLoaderElement.removeAttribute('aria-busy');
        }
        async _onChanged(event) {
            this.pillLoaderElement.removeAttribute('aria-busy');
            if (event.detail.cart['item_count'] === 0 || event.detail.quantity !== 0) {
                return;
            }
            let marginCompensation = 0;
            if (this.nextElementSibling) {
                marginCompensation = `-${getComputedStyle(this.nextElementSibling).paddingTop}`;
            }
            await animate4(
                this,
                { height: [`${this.clientHeight}px`, 0], marginBottom: [0, marginCompensation], overflow: 'hidden', opacity: [1, 0] },
                { duration: 0.2, easing: 'ease' }
            ).finished;
            this.remove();
        }
    };

    window.customElements.define('line-item', LineItem);
}

// js/common/cart/line-item-quantity.js
if (!window.customElements.get('line-item-quantity')) {
    var LineItemQuantity = class extends HTMLElement {
        constructor() {
            super();
            this.addEventListener('change', this._onQuantityChanged);
            this.addEventListener('click', this._onRemoveLinkClicked);
        }
        _onQuantityChanged(event) {
            if (!event.target.hasAttribute('data-line-key')) {
                return;
            }
            this._changeLineItemQuantity(event.target.getAttribute('data-line-key'), parseInt(event.target.value));
        }
        _onRemoveLinkClicked(event) {
            if (event.target.tagName !== 'A' || !event.target.href.includes('/cart/change')) {
                return;
            }
            event.preventDefault();
            const url = new URL(event.target.href);
            this._changeLineItemQuantity(url.searchParams.get('id'), parseInt(url.searchParams.get('quantity')));
        }
        async _changeLineItemQuantity(lineKey, targetQuantity) {
            const lineItem = this.closest('line-item');
            lineItem?.dispatchEvent(new CustomEvent('line-item:will-change', { bubbles: true, detail: { targetQuantity } }));
            let sectionsToBundle = [];
            document.documentElement.dispatchEvent(
                new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
            );
            const response = await fetch(`${Shopify.routes.root}cart/change.js`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    id: lineKey,
                    quantity: targetQuantity,
                    sections: sectionsToBundle,
                }),
            });
            if (!response.ok) {
                const responseContent = await response.json();
                this.closest('.line-item, tr').querySelector('[role="alert"]')?.remove();
                const errorSvg = `<svg width="14" viewBox="0 0 18 18">
        <path d="M0 9C0 4.02944 4.02944 0 9 0C13.9706 0 18 4.02944 18 9C18 13.9706 13.9706 18 9 18C4.02944 18 0 13.9706 0 9Z" fill="#eb001b"></path>
        <path d="M5.29289 6.70711L11.2929 12.7071L12.7071 11.2929L6.70711 5.29289L5.29289 6.70711ZM6.70711 12.7071L12.7071 6.70711L11.2929 5.2929L5.29289 11.2929L6.70711 12.7071Z" fill="#ffffff"></path>
      </svg>`;
                this.closest('.line-item, tr')
                    .querySelector('.line-item__info')
                    .insertAdjacentHTML(
                        'beforeend',
                        `<p class="text-with-icon text-error text-xs" role="alert">${errorSvg} ${responseContent['description']}</p>`
                    );
                this.querySelector('.quantity-input').value = this.querySelector('.quantity-input').defaultValue;
                lineItem?.dispatchEvent(new CustomEvent('line-item:error', { bubbles: true }));
            } else {
                const cartContent = await response.json();
                if (window.themeVariables.settings.pageType === 'cart') {
                    window.location.reload();
                } else {
                    const lineItemAfterChange = cartContent['items'].filter((lineItem2) => lineItem2['key'] === lineKey);
                    lineItem?.dispatchEvent(
                        new CustomEvent('line-item:change', {
                            bubbles: true,
                            detail: {
                                quantity: lineItemAfterChange.length === 0 ? 0 : lineItemAfterChange[0]['quantity'],
                                cart: cartContent,
                            },
                        })
                    );
                    document.documentElement.dispatchEvent(
                        new CustomEvent('cart:change', {
                            bubbles: true,
                            detail: {
                                baseEvent: 'line-item:change',
                                cart: cartContent,
                            },
                        })
                    );
                }
            }
        }
    };
    window.customElements.define('line-item-quantity', LineItemQuantity);
}

// js/common/cart/free-shipping-bar.js
if (!window.customElements.get('free-shipping-bar')) {
    var FreeShippingBar = class extends HTMLElement {
        static get observedAttributes() {
            return ['threshold', 'total-price'];
        }
        constructor() {
            super();
            this._onCartChangedListener = this._onCartChanged.bind(this);
            this._currencyFormatter = new Intl.NumberFormat(Shopify.locale, { style: 'currency', currency: Shopify.currency.active });
        }
        async connectedCallback() {
            this.threshold = Math.round(this.threshold * (Shopify.currency.rate || 1));
            document.addEventListener('cart:change', this._onCartChangedListener);
        }
        disconnectedCallback() {
            document.removeEventListener('cart:change', this._onCartChangedListener);
        }
        get threshold() {
            return parseFloat(this.getAttribute('threshold'));
        }
        set threshold(value) {
            this.setAttribute('threshold', value);
        }
        get totalPrice() {
            return parseFloat(this.getAttribute('total-price'));
        }
        set totalPrice(value) {
            this.setAttribute('total-price', value);
        }
        async attributeChangedCallback(attribute, oldValue, newValue) {
            await window.customElements.whenDefined('progress-bar');
            const progressBarElement = this.querySelector('progress-bar');
            switch (attribute) {
                case 'threshold':
                    progressBarElement.valueMax = newValue;
                    break;
                case 'total-price':
                    progressBarElement.valueNow = newValue;
                    break;
            }
            this._updateMessage();
        }
        _updateMessage() {
            const messageElement = this.querySelector('span');
            if (this.totalPrice >= this.threshold) {
                messageElement.innerHTML = this.getAttribute('reached-message');
            } else {
                const replacement = `<span class="bold text-accent">${this._currencyFormatter
                    .format((this.threshold - this.totalPrice) / 100)
                    .replace(/\$/g, '$$$$')}</span>`;
                messageElement.innerHTML = this.getAttribute('unreached-message').replace(new RegExp('({{.*}})', 'g'), replacement);
            }
        }
        _onCartChanged(event) {
            if (event.detail) {
                const priceForItems = event.detail['cart']['items']
                    .filter((item) => item['requires_shipping'])
                    .reduce((sum, item) => sum + item['final_line_price'], 0),
                    cartDiscount = event.detail['cart']['cart_level_discount_applications'].reduce(
                        (sum, discountAllocation) => sum + discountAllocation['total_allocated_amount'],
                        0
                    );
                this.totalPrice = priceForItems - cartDiscount;
            }
        }
    };
    window.customElements.define('free-shipping-bar', FreeShippingBar);
}

// js/common/feedback/progress-bar.js
if (!window.customElements.get('progress-bar')) {
    var ProgressBar = class extends HTMLElement {
        static get observedAttributes() {
            return ['aria-valuenow', 'aria-valuemax'];
        }
        set valueMax(value) {
            this.setAttribute('aria-valuemax', value);
        }
        set valueNow(value) {
            this.setAttribute('aria-valuenow', value);
        }
        attributeChangedCallback() {
            this.style.setProperty('--progress', `${Math.min(1, this.getAttribute('aria-valuenow') / this.getAttribute('aria-valuemax'))}`);
        }
    };
    window.customElements.define('progress-bar', ProgressBar);
}